// De schil: kopbalk met tabbladen, daaronder het actieve scherm.
//
// Vorm letterlijk uit de dummy (V1 r. 1614-1650): pagina-achtergrond #f5f5f5,
// een pillenbalk #f0f0f0 met radius 12, actief tabblad wit met een lichte
// schaduw, inactief #6b7280 en hover #e8e8e8. Inhoud in een kolom van maximaal
// 1400px met 24px/36px lucht.
//
// Wat anders is dan de dummy (W8 scherm 1, oordeel "anders — menu volgt W4"):
// zeven tabbladen in plaats van vier. Bedrijven staat er NIET: die hoort bij de
// AM-rol in V2 en heeft geen enkele databron. Workspace kwam erbij zodra W9 het
// gebruikersmodel legde. Beheer staat er sinds de inlog bestaat, en alleen voor
// wie de adminvlag draagt.
//
// SINDS DE INLOG (sessie 2): het dashboard opent op Workspace, want dat is
// W9-besluit B5 — de workspace is de landingspagina van je rol. Vóór de inlog
// opende het op Trends omdat er niemand ingelogd kón zijn; die reden is weg.

import { lazy, type ReactNode, Suspense, useEffect, useState } from "react";

import { BekijkAls } from "./components/BekijkAls";
import { ChatPopup } from "./components/ChatPopup";
import { PulseBar } from "./components/PulseBar";
import { TabVangnet } from "./components/TabVangnet";
import { useAdresParam, zetTab } from "./lib/adres";
import { SearchOverlay } from "./components/SearchOverlay";
import { ArbeidsmarktPage } from "./pages/ArbeidsmarktPage";
import { BeheerPagina } from "./pages/BeheerPagina";
import { FundingPage } from "./pages/FundingPage";
import { InlogPagina } from "./pages/InlogPagina";
import { SkillsPage } from "./pages/SkillsPage";
import { SourcesPage } from "./pages/SourcesPage";
import { TrendsPage } from "./pages/TrendsPage";
import { ValidatiePagina } from "./pages/ValidatiePagina";
import { VerkennenPagina } from "./pages/VerkennenPagina";
import { WorkspacePage } from "./pages/WorkspacePage";
import { api } from "./lib/api";
import { uitloggen } from "./lib/auth";
import { useMeta } from "./lib/meta";
import { magPlan, registerSectoren, startSector, useMij, vasteSector } from "./lib/mij";
import { zetActieveSector, zetVastePropositie } from "./lib/sectorkeuze";
import { magVerkennen } from "./lib/verkennenTab";
import { magKiezen, nieuwWeergave, zetNieuw } from "./nieuw/schakelaar";
import { zetTrendlaagStandaard } from "./nieuw/trendlaag/schakelaar";
import { NAAR_TAB } from "./lib/naarTab";
import type { Voorzet } from "./lib/voorzet";
import type { KaartLanding } from "./contracts";
import type { Mij, Skill, Source, Trend } from "./contracts";
import { color, font, radius, size, weight } from "./tokens";

const TABS = [
  "Workspace",
  "Skills",
  "Trends",
  "Arbeidsmarkt",
  "Verkennen",
  "Sources",
  "Funding",
  "Kanshebbers",
  "Plan",
  "Beheer",
] as const;
type Tab = (typeof TABS)[number];

// Zo heet een tab in het adres: het woord dat de gebruiker in het menu ziet.
const TAB_IN_ADRES: Record<Tab, string> = {
  Workspace: "start",
  Skills: "skills",
  Trends: "trends",
  Arbeidsmarkt: "arbeidsmarkt",
  Verkennen: "verkennen",
  Sources: "bronnen",
  Funding: "funding",
  Kanshebbers: "kanshebbers",
  Plan: "plan",
  Beheer: "beheer",
};

function tabUitAdres(woord: string | null): Tab | null {
  return TABS.find((t) => TAB_IN_ADRES[t] === woord) ?? null;
}

// VT-1: het validatiedocument staat op een eigen adres, zonder tabbladen. De
// server beslist wie het mag zien; dit adres is alleen de ingang.
const VALIDATIE_PAD = /^\/validatie\/([a-z_]+)\/?$/;
// De magic link komt terug op de voorpagina (Supabase kent alleen die als
// terugkeeradres). Wie op het validatieadres moest inloggen, wordt na het
// inloggen daarheen teruggezet. Local storage mag weigeren: dan land je gewoon
// op het dashboard en brengt de melding daar je verder.
const NA_INLOG = "sof.na_inlog";

function validatieVanPad(pad: string): string | null {
  return VALIDATIE_PAD.exec(pad)?.[1] ?? null;
}

function onthoudNaInlog(pad: string) {
  try {
    window.localStorage.setItem(NA_INLOG, pad);
  } catch {
    // niet te onthouden; de melding op het dashboard vangt het op
  }
}

/** Het onthouden validatieadres, één keer: daarna is het weg. */
function pakNaInlog(): string | null {
  try {
    const pad = window.localStorage.getItem(NA_INLOG);
    window.localStorage.removeItem(NA_INLOG);
    return pad && validatieVanPad(pad) ? pad : null;
  } catch {
    return null;
  }
}

function TabButton({
  tab,
  active,
  onSelect,
}: {
  tab: Tab;
  active: boolean;
  onSelect: (t: Tab) => void;
}) {
  return (
    <button
      onClick={() => onSelect(tab)}
      style={{
        background: active ? color.surface : "none",
        border: "none",
        fontFamily: "inherit",
        fontSize: size.lead,
        fontWeight: active ? weight.medium : weight.regular,
        color: active ? color.ink : color.soft,
        cursor: "pointer",
        padding: "8px 16px",
        borderRadius: radius.input,
        transition: "all 0.15s",
        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = "#e8e8e8";
          e.currentTarget.style.color = color.body;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = "none";
          e.currentTarget.style.color = color.soft;
        }
      }}
    >
      {tab}
    </button>
  );
}

/** De ingang naar de bronnenchat, in de kopbalk zodat hij op elk scherm bestaat.
 *  V2 opent de chat vanuit de workspace (r. 3708); V1 had er een knop voor in de
 *  filterbalk (r. 1724). Deze knop is die van V1, op de plek waar hij bij zeven
 *  tabbladen niet in de weg zit — en hij opent exact dezelfde overlay als de
 *  chatbalk in de workspace. */
function ChatKnop({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: color.surface,
        border: `1px solid ${color.border}`,
        color: color.body,
        padding: "6px 13px",
        borderRadius: radius.pill,
        fontSize: size.meta,
        fontWeight: weight.regular,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = color.border;
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: 5,
          background: color.ink,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
          <path d="M8 2v12M2 8h12" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
      Vraag het de bronnen
    </button>
  );
}

/** De zoekoverlay is niet van één scherm: hij zoekt over trends, skills én
 *  bronnen tegelijk (W8 scherm 3). Hij stond als `readOnly`-veld in de
 *  filterbalk van Trends, waar hij eruitzag als een lijstfilter en dat niet was.
 *  Sinds owner-review ronde 4 filtert dat veld op élk scherm de lijst zélf, en
 *  staat het zoeken-over-alles hier — naast de chatknop, want dat is de andere
 *  vraag die niet aan één tabblad hangt. */
function ZoekKnop({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Zoek in trends, vaardigheden én bronnen tegelijk."
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        background: color.surface,
        border: `1px solid ${color.border}`,
        color: color.body,
        padding: "6px 13px",
        borderRadius: radius.pill,
        fontSize: size.meta,
        fontFamily: "inherit",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = color.border;
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="6" cy="6" r="4" stroke={color.soft} strokeWidth="1.5" />
        <path d="M10 10l2 2" stroke={color.soft} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      Alles doorzoeken
    </button>
  );
}

/**
 * Wie er kijkt, rechts in de kopbalk. Bij een beheerder zonder rol staat er
 * "beheerder" en geen verzonnen functie: admin is een vlag, geen rol (W9-B3).
 */
function Account({ mij, storing }: { mij: Mij | null; storing: string | null }) {
  const [bezig, setBezig] = useState(false);
  if (!mij) {
    // Twee redenen waarom hier geen naam staat, en ze betekenen NIET hetzelfde:
    // een storing bij het ophalen is iets anders dan een omgeving zonder inlog.
    // Ze op één zin gooien zou de fout wegpoetsen tot een eigenschap.
    return (
      <span
        style={{ fontSize: size.meta, color: storing ? color.accentAmber : color.faint }}
        title={storing ?? undefined}
      >
        {storing ? "wie je bent kon niet worden opgehaald" : "geen inlog op deze omgeving"}
      </span>
    );
  }
  const onder = mij.rolLabel ?? (mij.isAdmin ? "beheerder" : null);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ textAlign: "right", lineHeight: 1.25 }}>
        <div style={{ fontSize: size.meta, color: color.ink, fontWeight: weight.medium }}>
          {mij.naam}
        </div>
        {onder && <div style={{ fontSize: size.label, color: color.faint }}>{onder}</div>}
      </div>
      <button
        onClick={() => {
          setBezig(true);
          void uitloggen();
        }}
        disabled={bezig}
        title="Uitloggen"
        aria-label="Uitloggen"
        style={{
          background: "none",
          border: `1px solid ${color.border}`,
          borderRadius: radius.input,
          padding: "6px 8px",
          cursor: bezig ? "default" : "pointer",
          color: color.soft,
          lineHeight: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 2H3.5A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14H6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <path
            d="M10.5 11L14 8l-3.5-3M14 8H6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * De poort. Vier standen, vier antwoorden — geen witte pagina en geen
 * doorlaten "voor de zekerheid".
 *
 * `geen_inlog` is niet altijd een fout: draait de dienst met SOF_DEV_MODE, dan
 * antwoordt `/api/mij` gewoon en komen we hier niet. Draait hij mét auth en heb
 * je geen sessie, dan hoort het inlogscherm er te staan.
 */
export function App() {
  const stand = useMij();
  let validatie = validatieVanPad(window.location.pathname);

  if (stand.stand === "laden") return <Wachten />;
  // De sector waar alle schermen op staan (ADR 2026-10-23-ict-los-platform).
  // Hier, vóór er één scherm staat: dan vraagt niets eerst ICT en daarna de eigen.
  zetActieveSector(startSector(stand.stand === "ingelogd" ? stand.mij : null));
  // Accountmanager en business partner: de propositie staat vast, zonder menu.
  zetVastePropositie(vasteSector(stand.stand === "ingelogd" ? stand.mij : null));
  // De trendlaag is standaard aan voor wie is ingelogd en geen beheerder is.
  // Ook hier, vóór er één scherm of inhoudelijke aanvraag staat: zo knippert
  // niets tussen oud en nieuw.
  zetTrendlaagStandaard(stand.stand === "ingelogd" && !stand.mij.isAdmin);
  if (stand.stand === "geen_inlog") {
    if (validatie) onthoudNaInlog(window.location.pathname);
    return <InlogPagina />;
  }
  if (stand.stand === "geen_toegang") return <InlogPagina geenToegang={stand.reden} />;
  if (stand.stand === "ingelogd" && !validatie) {
    const terug = pakNaInlog();
    if (terug) {
      window.history.replaceState(null, "", terug);
      validatie = validatieVanPad(terug);
    }
  }
  if (validatie) return <ValidatiePagina propositie={validatie} />;
  // Een storing mag de deur niet dichtgooien: het dashboard leest ook zonder
  // profiel. De reden reist wel mee, zodat de kopbalk 'kon niet worden
  // opgehaald' zegt en niet 'geen inlog' — dat zijn twee verschillende dingen.
  return (
    <Dashboard
      mij={stand.stand === "ingelogd" ? stand.mij : null}
      storing={stand.stand === "fout" ? stand.reden : null}
    />
  );
}

function Wachten() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f5f5",
        fontFamily: font,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: color.faint,
        fontSize: size.meta,
      }}
    >
      Even kijken wie je bent…
    </div>
  );
}

/** Naar de nieuwe weergave (proef): alleen voor beheer. */
function NieuwKnop() {
  return (
    <button
      type="button"
      onClick={() => zetNieuw(true)}
      title="Bekijk het platform in de nieuwe vormgeving (proef)"
      style={{
        background: color.ink,
        color: "#fff",
        border: "none",
        borderRadius: radius.input,
        padding: "6px 10px",
        fontSize: size.meta,
        fontFamily: font,
        cursor: "pointer",
      }}
    >
      Nieuwe weergave
    </button>
  );
}

// De nieuwe weergave (frontend-afronden, 13-10) als los te laden deel.
type NieuwModule = typeof import("./nieuw");

// Het Plan is editie 19 voor iedereen met Plan-toegang, ook zonder de
// schakelaar (owner 21-10; editie 11 is weg). Los te laden: de rest van de
// huidige weergave krijgt er geen regel van mee.
const PlanLos = lazy(() => import("./nieuw/plan-los"));

function Dashboard({ mij, storing }: { mij: Mij | null; storing: string | null }) {
  // De tab staat in het adres (?tab=trends): terug in de browser gaat naar de
  // vorige tab, herladen blijft op dezelfde (owner 03-11).
  const [tabInAdres] = useAdresParam("tab");
  const uitAdres = tabUitAdres(tabInAdres);
  // Een tab die je niet mag zien (Beheer, Plan) opent ook niet via het adres.
  const magTab = (t: Tab) => (t !== "Beheer" || !!mij?.isAdmin) && ((t !== "Plan" && t !== "Kanshebbers") || magPlan(mij));
  const activeTab: Tab = uitAdres && magTab(uitAdres) ? uitAdres : mij ? "Workspace" : "Trends";
  // Eén stap: de nieuwe tab erin, de eigen parameters van de vorige tab eruit.
  const setActiveTab = (t: Tab) => zetTab(TAB_IN_ADRES[t]);
  // Pilot gereed 26-10 (1C): Verkennen alleen op een zichtbare sector waar de
  // graaf voor bestaat (/api/meta verkennenSectoren); ICT verborgen = weg.
  const verkennen = magVerkennen(useMeta());
  // Accountmanagement en business partners zien altijd de nieuwe weergave, zonder
  // knop. Beheer houdt de schakelaar "Nieuwe weergave (proef)"; uit = precies
  // het platform zoals het live staat.
  const nieuw = nieuwWeergave(mij);
  const [nieuwMod, setNieuwMod] = useState<NieuwModule | null>(null);
  useEffect(() => {
    if (nieuw) void import("./nieuw").then(setNieuwMod);
  }, [nieuw]);
  const P = nieuw ? nieuwMod?.paginas : undefined;
  // Een scherm dat nog niet vernieuwd is, staat binnen het nieuwe kader.
  const oud = (el: ReactNode) => (nieuw ? <div className="n-oud">{el}</div> : el);
  const [chatOpen, setChatOpen] = useState(false);
  const [zoekOpen, setZoekOpen] = useState(false);
  // Een treffer uit de zoekoverlay hoort op zijn eigen tabblad open te gaan.
  // Vóór ronde 4 was alleen `onSelectTrend` bedraad: een skill of bron
  // aanklikken deed niets — een dode klik in een lijst die er wél stond.
  const [trefferTrend, setTrefferTrend] = useState<Trend | null>(null);
  const [trefferSkill, setTrefferSkill] = useState<Skill | null>(null);
  const [trefferBron, setTrefferBron] = useState<Source | null>(null);
  const [chatVraag, setChatVraag] = useState<string | null>(null);
  // De rol waarin de bezoeker kijkt. Staat hier en niet in de workspace omdat
  // hij twee schermen aanstuurt — V2 doet het net zo (`roleKey` in de app-shell
  // voedt zowel de workspace als de chat). Zolang er geen inlog is, is dit een
  // VOORBEELDkeuze en zegt de workspace dat er ook bij.
  const [rol, setRol] = useState<string | null>(null);
  // Een filter dat een tabblad krijgt voorgezet: de doorklik onder een
  // chatantwoord ("bekijk alle 43 op de Skills-tab") en de tab-landing van een
  // redactiekaart. Per tabblad apart, want ze staan naast elkaar en een filter
  // voor Skills hoort Trends niet te raken.
  const [voorzetTrends, setVoorzetTrends] = useState<Voorzet | null>(null);
  const [voorzetSkills, setVoorzetSkills] = useState<Voorzet | null>(null);
  const [voorzetBronnen, setVoorzetBronnen] = useState<Voorzet | null>(null);
  // W5 (funding werkend): de link "alle N op de Funding-tab" in het skill-detail.
  const [voorzetFunding, setVoorzetFunding] = useState<Voorzet | null>(null);

  // Het gebruikslog (W6, research §4): welk tabblad een ingelogde gebruiker
  // opent — ook het eerste bij het laden. Alleen metadata, nooit inhoud. Zonder
  // inlog (ontwikkelmodus) is er niemand om aan te tellen, dus dan niets.
  // Vuur-en-vergeet: een telling die faalt mag het scherm nooit raken.
  const mijId = mij?.id;
  useEffect(() => {
    if (!mijId) return;
    void api.gebruik({ scherm: activeTab }).catch(() => {});
  }, [activeTab, mijId]);

  // De doorklik van een redactiekaart. Elke kaart HEEFT een landingsplek (de
  // lane laat een kandidaat zonder landing niet door), dus dit is de enige
  // plek waar hij nog stuk kan gaan — vandaar dat elke tak hier echt iets doet
  // en de laatste terugvalt op het tabblad zelf in plaats van op niets.
  //
  // De detailpanelen krijgen een VOLLEDIGE entiteit mee, geen id: TrendDetail
  // en SkillDetail renderen wat ze aangereikt krijgen en halen niets zelf op.
  // Daarom halen we het detail hier op vóór we het paneel openen.
  const openLanding = async (landing: KaartLanding) => {
    if (landing.soort === "chat") {
      setChatVraag(landing.vraag ?? null);
      setChatOpen(true);
      return;
    }
    if (landing.soort === "reeks") {
      setActiveTab("Arbeidsmarkt");
      return;
    }
    const tab = landing.tab ?? "";
    if (landing.soort === "paneel" && landing.id) {
      try {
        if (tab === "trends") {
          setTrefferTrend((await api.trend(landing.id)).trend);
          setActiveTab("Trends");
          return;
        }
        if (tab === "skills") {
          setTrefferSkill((await api.skill(landing.id)).skill);
          setActiveTab("Skills");
          return;
        }
        if (tab === "sources") {
          setTrefferBron((await api.source(landing.id)).source);
          setActiveTab("Sources");
          return;
        }
      } catch {
        // Ophalen mislukt: dan nog steeds naar het tabblad, want daar staat het
        // onderwerp in de lijst. Een dode klik is het slechtste antwoord.
      }
    }
    // `soort: "tab"` mét filter: het tabblad opent MET dat filter aan. Dit was
    // in het contract al beschreven en werd hier stilzwijgend genegeerd —
    // waardoor de doorklik van een redactiekaart op een ongefilterde lijst
    // uitkwam. De chat heeft hem nu ook nodig: zonder voorgezet filter toont
    // de Skills-tab een ander getal dan het antwoord noemde, en dan is dat
    // getal niet meer na te tellen.
    //
    // Een NIEUW object per klik, ook als het filter identiek is: de pagina's
    // reageren op objectidentiteit, dus twee keer dezelfde doorklik moet twee
    // keer werken.
    const filter = landing.filter ? { ...landing.filter } : null;
    if (filter) {
      if (tab === "trends") setVoorzetTrends(filter);
      if (tab === "skills") setVoorzetSkills(filter);
      if (tab === "sources") setVoorzetBronnen(filter);
      if (tab === "funding") setVoorzetFunding(filter);
    }
    const doel: Record<string, Tab> = {
      trends: "Trends",
      skills: "Skills",
      sources: "Sources",
      arbeidsmarkt: "Arbeidsmarkt",
      // 4B (feedbackronde 27-08): de platform-routes onder "Verder verkennen"
      // kunnen ook naar de Funding-tab wijzen; B7: de editie-verwijzing
      // onder een chatantwoord landt op de Workspace.
      funding: "Funding",
      workspace: "Workspace",
      // De workspace-kaart "Marktbewerkingsplan ICT" (voorproef 30-09).
      plan: "Plan",
    };
    if (doel[tab]) setActiveTab(doel[tab]);
  };

  // W5: panelen zonder aangereikte navigatie (SkillDetail) sturen een landing
  // als venster-gebeurtenis (lib/naarTab.ts); die gaat door dezelfde openLanding.
  useEffect(() => {
    const luister = (e: Event) => {
      const landing = (e as CustomEvent<KaartLanding>).detail;
      if (landing) void openLanding(landing);
    };
    window.addEventListener(NAAR_TAB, luister);
    return () => window.removeEventListener(NAAR_TAB, luister);
  });

  const inhoud = (
    <TabVangnet key={activeTab} tab={activeTab}>
        {/* Scherm 2: de pulsbalk hoort boven élk scherm, niet alleen boven Skills.
            In de nieuwe weergave staat hij per pagina (versie 4: één regel). */}
        {!nieuw && <PulseBar />}
        {mij?.rol === "accountmanagement" && mij.propositie && <ValidatieMelding />}
        {activeTab === "Workspace" &&
          (() => {
            const props = {
              onChat: () => {
                setChatVraag(null);
                setChatOpen(true);
              },
              onOpen: openLanding,
              rol,
              onRol: setRol,
            };
            return P?.Start ? <P.Start {...props} /> : oud(<WorkspacePage {...props} />);
          })()}
        {activeTab === "Beheer" && mij?.isAdmin && oud(<BeheerPagina mijnEmail={mij.email} />)}
        {activeTab === "Trends" &&
          (P?.Trends ? (
            <P.Trends openen={trefferTrend} voorzet={voorzetTrends} />
          ) : (
            oud(<TrendsPage openen={trefferTrend} voorzet={voorzetTrends} />)
          ))}
        {activeTab === "Skills" &&
          (P?.Skills ? (
            <P.Skills openen={trefferSkill} voorzet={voorzetSkills} />
          ) : (
            oud(<SkillsPage openen={trefferSkill} voorzet={voorzetSkills} />)
          ))}
        {activeTab === "Sources" &&
          (P?.Bronnen ? (
            <P.Bronnen openen={trefferBron} voorzet={voorzetBronnen} />
          ) : (
            oud(<SourcesPage openen={trefferBron} voorzet={voorzetBronnen} />)
          ))}
        {activeTab === "Funding" &&
          (P?.Funding ? <P.Funding voorzet={voorzetFunding} /> : oud(<FundingPage voorzet={voorzetFunding} />))}
        {/* Kanshebbers bestaat alleen in de nieuwe weergave (owner 05-11, snelle versie). */}
        {activeTab === "Kanshebbers" && magPlan(mij) && P?.Kanshebbers && <P.Kanshebbers />}
        {activeTab === "Plan" &&
          magPlan(mij) &&
          (() => {
            // De MBP-strateeg is de bestaande bronnenchat: de vraag landt
            // voorgeladen, met de inleiding en het open hoofdstuk erbij.
            const onVraag = (vraag: string) => {
              setChatVraag(vraag);
              setChatOpen(true);
            };
            if (P?.Plan) return <P.Plan onVraag={onVraag} />;
            return nieuw ? null : (
              <Suspense fallback={null}>
                <PlanLos onVraag={onVraag} />
              </Suspense>
            );
          })()}
        {activeTab === "Arbeidsmarkt" && (P?.Arbeidsmarkt ? <P.Arbeidsmarkt /> : oud(<ArbeidsmarktPage />))}
        {activeTab === "Verkennen" &&
          verkennen &&
          (() => {
            const props = {
              // Een gebied op de kaart wordt een echte vraag in de bestaande
              // chat — niet een tweede chat, en niet een dode knop.
              onVraag: (vraag: string) => {
                setChatVraag(vraag);
                setChatOpen(true);
              },
              // Bouwen → "Maak er een plan van": naar het plan-scherm, alleen voor wie het mag zien.
              onPlan: magPlan(mij) ? () => setActiveTab("Plan") : undefined,
            };
            return P?.Verkennen ? <P.Verkennen {...props} /> : oud(<VerkennenPagina {...props} />);
          })()}
    </TabVangnet>
  );
  const lagen = (
    <>
      {zoekOpen && (
        <SearchOverlay
          onClose={() => setZoekOpen(false)}
          onSelectTrend={(t) => {
            setZoekOpen(false);
            setActiveTab("Trends");
            setTrefferTrend(t);
          }}
          onSelectSkill={(s2) => {
            setZoekOpen(false);
            setActiveTab("Skills");
            setTrefferSkill(s2);
          }}
          onSelectSource={(s2) => {
            setZoekOpen(false);
            setActiveTab("Sources");
            setTrefferBron(s2);
          }}
          regelSectoren={registerSectoren(mij)}
          onSelectFunding={(f) => {
            // Testfeedback 03-11 (rij 3): de regeling opent op de Funding-tab,
            // met haar naam in het zoekveld en het detail al open.
            setZoekOpen(false);
            setVoorzetFunding({ zoek: f.naam, regeling: f });
            setActiveTab("Funding");
          }}
        />
      )}

      <ChatPopup
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        initieleVraag={chatVraag}
        onVraagGebruikt={() => setChatVraag(null)}
        rol={rol}
        nieuweWeergave={nieuw}
        onOpen={(landing) => {
          // Een doorklik uit de chat sluit de chat: het antwoord blijft staan
          // als je hem opnieuw opent, maar je wilt zien waar je heen ging.
          setChatOpen(false);
          void openLanding(landing);
        }}
      />
    </>
  );

  if (nieuw) {
    if (!nieuwMod) return <div className="n-laden" />;
    const { Kader } = nieuwMod;
    return (
      <>
        <Kader
          mij={mij}
          tab={activeTab}
          onTab={setActiveTab}
          magPlan={magPlan(mij)}
          magVerkennen={verkennen}
          onZoek={() => setZoekOpen(true)}
          onChat={() => {
            setChatVraag(null);
            setChatOpen(true);
          }}
          onUitloggen={() => void uitloggen()}
        >
          {inhoud}
        </Kader>
        {lagen}
      </>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", fontFamily: font }}>
      <div style={{ background: "#f5f5f5", padding: "0 36px" }}>
        <div
          style={{
            background: "#f0f0f0",
            borderRadius: radius.card,
            padding: "8px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            // Op een smal scherm breekt de balk af in plaats van de pagina
            // breder te maken dan het scherm (Playwright-proef 30-09, mobiel).
            flexWrap: "wrap",
            gap: 8,
            marginTop: 8,
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center", overflowX: "auto", maxWidth: "100%" }}>
            {/* Beheer alleen voor wie de vlag draagt. Verbergen is geen
                beveiliging — de service weigert elke beheerroute zonder admin —
                maar een knop tonen die 403 geeft is een belofte die breekt. */}
            {TABS.filter((t) => (t !== "Beheer" || mij?.isAdmin) && (t !== "Plan" || magPlan(mij)) && (t !== "Verkennen" || verkennen) && t !== "Kanshebbers").map((t) => (
              <TabButton key={t} tab={t} active={activeTab === t} onSelect={setActiveTab} />
            ))}
          </div>
          {/* Rechts stond in de dummy een bewaar-hart (vervalt in v1) en een
              VERZONNEN account. Hier staat nu de echte naam uit /api/mij — of,
              in ontwikkelmodus, de eerlijke mededeling dat er geen inlog is. */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
            <ZoekKnop onClick={() => setZoekOpen(true)} />
            {/* De kopbalk-knop opent altijd SCHOON: zonder deze reset vulde
                hij een feed-vraag van tien minuten geleden opnieuw in
                (feed→chat-gat 4, ontwerp 26-08). */}
            <ChatKnop
              onClick={() => {
                setChatVraag(null);
                setChatOpen(true);
              }}
            />
            {magKiezen(mij) && <NieuwKnop />}
            <BekijkAls mij={mij} />
            <Account mij={mij} storing={storing} />
          </div>
        </div>
      </div>

      <div style={{ padding: "24px 36px", maxWidth: 1400, margin: "0 auto" }}>
        {inhoud}
      </div>

      {lagen}
    </div>
  );
}

/**
 * VT-1: een accountmanager die inlogt, ziet hier dat er een vraag voor hem
 * klaarligt — ook als hij niet via het validatieadres binnenkwam. Release
 * 06-10: de server zegt welke documenten bij zijn school horen (een
 * pilotpropositie kan meer scholen raken); de eerste met nog iets open komt
 * hier. Geen document of alles beantwoord: dan staat er niets.
 */
function ValidatieMelding() {
  const [open, setOpen] = useState<{ titel: string; tekst: string; pagina: string } | null>(
    null,
  );
  useEffect(() => {
    let levend = true;
    api
      .validatieLijst()
      .then((l) => {
        // Testfeedback 03-11: de balk staat alleen aan als de beheerder hem
        // aanzet (Beheer › Configuratie › Meldingen), bij een nieuwe propositie.
        if (!l.melding) return;
        const d = l.documenten.find((x) => x.voortgang.beantwoord < x.voortgang.totaal);
        if (levend && d) setOpen({ titel: d.titel, tekst: d.voortgang.tekst, pagina: d.pagina });
      })
      .catch(() => {});
    return () => {
      levend = false;
    };
  }, []);
  if (!open) return null;
  return (
    <div
      role="status"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        background: color.accentAmberBg,
        borderRadius: radius.block,
        padding: "10px 16px",
        margin: "12px 0",
        fontSize: size.body,
        color: color.body,
      }}
    >
      <span>
        <b style={{ color: color.ink }}>Er ligt een vraag voor je klaar:</b> {open.titel}{" "}
        <span style={{ color: color.soft }}>({open.tekst})</span>
      </span>
      <a
        href={open.pagina}
        style={{ color: color.ink, fontWeight: weight.medium }}
      >
        Openen →
      </a>
    </div>
  );
}
